import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useQueryClient } from 'react-query';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { PageChromeProvider, usePageChrome } from '../context/PageChromeContext';
import {
  FiMenu,
  FiX,
  FiFileText,
  FiLayers,
  FiMail,
  FiBookOpen,
  FiChevronsLeft,
  FiChevronsRight,
  FiUserCheck,
  FiClipboard,
  FiShoppingBag,
  FiCheckSquare,
  FiPackage,
  FiPieChart,
  FiDollarSign,
  FiTruck,
  FiTrendingUp,
  FiFolder,
} from 'react-icons/fi';
import { APP_UI_VERSION } from '../version';
import NotificationBell from './NotificationBell';
import BrandLogo from './BrandLogo';
import UserMenu from './UserMenu';
import { PageTransition, MotionSidebarItem } from './motion';
import { useProductAnalyticsTracking } from '../hooks/useProductAnalytics';
import { titleFromPath } from '../utils/pageTitles';
import { resolveActiveMenuPath } from '../utils/sidebarActivePath';
import useAutoHideScrollbar from '../hooks/useAutoHideScrollbar';
import './Layout.css';
import { appAlert } from '../utils/appDialog';
import { PRODUCT_DISPLAY_NAME } from '../config/product';

const SIDEBAR_COLLAPSED_KEY = 'gobunnyy-sidebar-collapsed';

function displayName(u) {
  const name = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
  return name || u?.email || 'user';
}

// Workspace IA (WP0): labels/grouping only — URLs and permissions unchanged.
const MENU_ITEMS = [
  { path: '/customers', label: 'Customers', icon: FiUserCheck, tone: 'green', roles: ['consultant', 'admin'], permission: 'tab_customers', section: 'Revenue' },
  { path: '/crm?tab=pipeline', label: 'Sales pipeline', icon: FiTrendingUp, tone: 'mint', roles: ['consultant', 'admin'], anyOf: ['tab_crm_pipeline', 'crm_pipeline_view'], section: 'Revenue' },
  { path: '/requisitions', label: 'Requisitions', icon: FiCheckSquare, tone: 'orange', roles: ['consultant', 'admin'], permission: 'tab_requisitions', section: 'Revenue' },
  { path: '/orders', label: 'Client POs', icon: FiClipboard, tone: 'brown', roles: ['consultant', 'admin'], permission: 'tab_orders', section: 'Revenue' },
  { path: '/erp/selling', label: 'Sales', icon: FiFileText, tone: 'blue', roles: ['consultant', 'admin'], anyOf: ['tab_erp', 'erp_selling_view', 'service_sales_view'], matchPaths: ['/erp/selling', '/sales'], section: 'Revenue' },
  { path: '/services', label: 'Services', icon: FiPackage, tone: 'indigo', roles: ['candidate', 'consultant', 'admin'], permission: 'tab_services', section: 'Revenue' },
  { path: '/board', label: 'Board', icon: FiBookOpen, tone: 'purple', roles: ['candidate', 'consultant', 'admin'], permission: 'tab_board', section: 'Revenue' },
  { path: '/projects', label: 'Projects', icon: FiFolder, tone: 'orange', roles: ['consultant', 'admin'], permission: 'tab_projects', section: 'Delivery' },
  { path: '/vendors', label: 'Vendors', icon: FiTruck, tone: 'brown', roles: ['consultant', 'admin'], permission: 'tab_vendors', section: 'Procurement' },
  { path: '/buying', label: 'Buying', icon: FiShoppingBag, tone: 'orange', roles: ['consultant', 'admin'], permission: 'tab_buying', section: 'Procurement' },
  { path: '/erp', label: 'Finance overview', icon: FiPieChart, tone: 'green', roles: ['consultant', 'admin'], permission: 'tab_erp', section: 'Finance' },
  { path: '/erp/accounting', label: 'Accounting', icon: FiDollarSign, tone: 'mint', roles: ['consultant', 'admin'], permission: 'tab_accounting', section: 'Finance' },
  { path: '/finance/payouts', label: 'Consultant payouts', icon: FiDollarSign, tone: 'teal', roles: ['consultant', 'admin'], anyOf: ['service_finance_view', 'service_finance_manage'], section: 'Finance' },
  { path: '/metadata', label: 'Metadata', icon: FiLayers, tone: 'indigo', roles: ['admin'], permission: 'tab_metadata', section: 'Admin' },
  { path: '/email-queue', label: 'Email queue', icon: FiMail, tone: 'blue', roles: ['admin'], permission: 'tab_email_queue', section: 'Admin' },
];

const LayoutShell = ({ children }) => {
  const { user, logout, impersonation, isImpersonating, stopImpersonating } = useAuth();
  const {
    hasPermission,
    hasAnyPermission,
    loading: permissionsLoading,
    usedFallback: permissionsUnavailable,
  } = usePermissions();
  const { overrideTitle } = usePageChrome();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useProductAnalyticsTracking();
  useAutoHideScrollbar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [exitingImpersonation, setExitingImpersonation] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonating = async () => {
    setExitingImpersonation(true);
    try {
      await stopImpersonating();
      queryClient.clear();
      navigate('/');
    } catch (err) {
      console.error('Failed to stop impersonation:', err);
      void appAlert(err?.response?.data?.error || 'Failed to return to admin session');
    } finally {
      setExitingImpersonation(false);
    }
  };

  const filteredMenuItems = useMemo(
    () =>
      MENU_ITEMS.filter((item) => {
        const role = user?.role;
        if (!role) return false;

        const roles = item.roles || [];
        const candidateOnly = roles.length === 1 && roles[0] === 'candidate';
        const adminOnly = roles.length > 0 && roles.every((r) => r === 'admin');

        // Candidates only see candidate-allowed items
        if (role === 'candidate') {
          if (!roles.includes('candidate')) return false;
        } else if (candidateOnly) {
          return false;
        } else if (adminOnly && role !== 'admin') {
          // Admin-only chrome stays admin-gated (Metadata, scanner, …)
          return false;
        }

        if (permissionsLoading) {
          return roles.includes(role) || (!adminOnly && !candidateOnly && role !== 'candidate');
        }

        if (item.anyOf?.length) return hasAnyPermission(item.anyOf);
        if (item.permission) return hasPermission(item.permission);
        return roles.includes(role);
      }),
    [user?.role, permissionsLoading, hasPermission, hasAnyPermission]
  );

  const menuSections = useMemo(() => {
    const order = [];
    const map = new Map();
    for (const item of filteredMenuItems) {
      const key = item.section || '';
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key).push(item);
    }
    return order.map((name) => ({ name, items: map.get(name) }));
  }, [filteredMenuItems]);

  const activeMenuPath = useMemo(
    () => resolveActiveMenuPath(filteredMenuItems, location.pathname, location.search),
    [filteredMenuItems, location.pathname, location.search]
  );

  const pageTitle = useMemo(() => {
    if (overrideTitle) return overrideTitle;
    const params = new URLSearchParams(location.search);
    if (location.pathname === '/crm' && params.get('tab') === 'pipeline') {
      return 'Sales pipeline';
    }
    return titleFromPath(location.pathname, MENU_ITEMS);
  }, [overrideTitle, location.pathname, location.search]);

  useEffect(() => {
    document.title = `${pageTitle} · GoSuite`;
  }, [pageTitle]);

  return (
    <div className={`layout layout--top-title ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''}`}>
      <div className="layout-shell">
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              key="sidebar-backdrop"
              className="sidebar-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setSidebarOpen(false)}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>
        <aside className={`sidebar layer-mid ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-brand-row">
            <Link
              to="/"
              className="sidebar-brand-link"
              onClick={() => setSidebarOpen(false)}
              aria-label="Go to dashboard home"
              title="Dashboard home"
            >
              <BrandLogo variant={sidebarCollapsed ? 'compact' : 'navbar'} />
            </Link>
            <button
              type="button"
              className="sidebar-collapse-toggle"
              onClick={() => setSidebarCollapsed((c) => !c)}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
            </button>
          </div>
          <nav className="sidebar-nav" aria-label="Primary">
            {menuSections.map((section) => (
              <div
                key={section.name || 'main'}
                className="sidebar-section"
                role="group"
                aria-label={section.name || undefined}
              >
                {section.name && !sidebarCollapsed ? (
                  <div className="sidebar-section-label">{section.name}</div>
                ) : null}
                <div className="sidebar-section-items">
                  {section.items.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = item.path === activeMenuPath;
                    return (
                      <MotionSidebarItem
                        key={item.path}
                        className="sidebar-item-wrap"
                        isActive={isActive}
                        delay={index * 0.03}
                      >
                        <Link
                          to={item.path}
                          className={`sidebar-item ${isActive ? 'active' : ''}`}
                          title={item.label}
                          aria-label={item.label}
                          aria-current={isActive ? 'page' : undefined}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <span className={`nav-glyph nav-glyph--${item.tone || 'blue'}`} aria-hidden="true">
                            <Icon />
                          </span>
                          <span className="sidebar-item-label">{item.label}</span>
                        </Link>
                      </MotionSidebarItem>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        <div className="layout-main">
          {isImpersonating && (
            <div className="impersonation-banner" role="status">
              <span>
                Viewing as <strong>{displayName(user)}</strong>
                {impersonation?.impersonator && (
                  <>
                    {' '}
                    (admin: {displayName(impersonation.impersonator)})
                  </>
                )}
              </span>
              <button
                type="button"
                className="impersonation-banner-exit"
                onClick={handleStopImpersonating}
                disabled={exitingImpersonation}
              >
                {exitingImpersonation ? 'Returning…' : 'Return to admin'}
              </button>
            </div>
          )}
          <nav className="navbar layer-front" aria-label="Top bar">
            <div className="navbar-content">
              <button
                type="button"
                className="menu-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
              >
                {sidebarOpen ? <FiX /> : <FiMenu />}
              </button>
              <Link
                to="/"
                className="navbar-brand"
                onClick={() => setSidebarOpen(false)}
                aria-label={`${PRODUCT_DISPLAY_NAME} by GoBunnyy home`}
              >
                <BrandLogo variant="navbar" decorative />
              </Link>
              <h1 className="navbar-page-title">{pageTitle}</h1>
              <div className="navbar-trailing">
                <NotificationBell />
                <UserMenu onLogout={handleLogout} />
              </div>
            </div>
          </nav>

          <main className="main-content layer-recess scroll-auto-hide">
            {permissionsUnavailable ? (
              <div className="layout-permissions-banner" role="status">
                Permissions could not be loaded — showing default access for your role. Some actions
                may be rejected by the server until the connection recovers.
              </div>
            ) : null}
            <div className="page-stage">
              <PageTransition>{children}</PageTransition>
            </div>
            <div
              className="layout-build-stamp"
              title="If this version does not change after deploy, the browser or CDN is still serving an old index.html or bundle."
            >
              UI {APP_UI_VERSION}
              {process.env.REACT_APP_BUILD_REF ? ` · ${process.env.REACT_APP_BUILD_REF}` : ''}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

const Layout = ({ children }) => (
  <PageChromeProvider>
    <LayoutShell>{children}</LayoutShell>
  </PageChromeProvider>
);

export default Layout;
